import { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { cn } from "@/lib/cn";
import { fileKind, planImportFiles } from "@/components/accounts/importPlan";

/**
 * Drop anything the seller shipped: archives, a whole folder of them, loose
 * `.session` files, their `<phone>.json` companions. Folders are walked
 * recursively; a click opens the file picker, the secondary button opens the
 * native folder picker (Safari's drop event silently discards directories).
 */
export function ImportDropzone({
  files,
  onChange,
}: {
  files: File[];
  onChange: (next: File[]) => void;
}) {
  const { t } = useTranslation();
  const inputId = useId();
  const fileRef = useRef<HTMLInputElement>(null);
  const folderRef = useRef<HTMLInputElement>(null);
  const [isOver, setIsOver] = useState(false);

  const merge = (incoming: File[]) => {
    if (incoming.length === 0) return;
    // Dedupe by name + size — the same file dropped twice shouldn't queue
    // twice. The backend rejects true duplicates by phone/tg_user_id anyway.
    const seen = new Set(files.map((f) => `${f.name}|${f.size}`));
    const fresh = incoming.filter((f) => {
      const key = `${f.name}|${f.size}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    if (fresh.length > 0) onChange([...files, ...fresh]);
  };

  const handleDrop = async (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setIsOver(false);
    const items = Array.from(e.dataTransfer.items);
    const collected: File[] = [];
    const entries = items
      .map((it) => it.webkitGetAsEntry?.())
      .filter((entry): entry is FileSystemEntry => entry != null);

    if (entries.length > 0) {
      for (const entry of entries) collected.push(...(await walkEntry(entry)));
    } else {
      collected.push(...Array.from(e.dataTransfer.files));
    }
    merge(collected);
  };

  const plan = planImportFiles(files);

  return (
    <div className="space-y-2">
      <label
        htmlFor={inputId}
        onDragOver={(e) => {
          e.preventDefault();
          setIsOver(true);
        }}
        onDragLeave={() => setIsOver(false)}
        onDrop={handleDrop}
        className={cn(
          "flex cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed px-4 py-5 text-center transition-colors",
          isOver
            ? "border-accent bg-accent/10 text-accent"
            : "border-border bg-bg-primary/40 text-fg-secondary hover:border-fg-muted",
        )}
      >
        <p className="text-sm font-medium">{t("accounts.import.drop.title")}</p>
        <p className="text-xs leading-snug text-fg-muted">
          {t("accounts.import.drop.hint")}
        </p>
        <input
          id={inputId}
          ref={fileRef}
          type="file"
          accept=".zip,.7z,.session,.json,application/zip,application/x-zip-compressed,application/x-7z-compressed,application/json"
          multiple
          className="hidden"
          onChange={(e) => merge(e.target.files ? Array.from(e.target.files) : [])}
        />
      </label>

      <div className="flex items-center justify-between text-xs text-fg-muted">
        <button
          type="button"
          onClick={() => folderRef.current?.click()}
          className="text-accent hover:underline"
        >
          {t("accounts.import.drop.pick_folder")}
        </button>
        <input
          ref={folderRef}
          type="file"
          // Non-standard, but every major browser turns this into a folder picker.
          // @ts-expect-error — not in React's attribute types
          webkitdirectory=""
          directory=""
          multiple
          className="hidden"
          onChange={(e) => merge(e.target.files ? Array.from(e.target.files) : [])}
        />
        {files.length > 0 && (
          <button
            type="button"
            onClick={() => onChange([])}
            className="text-fg-muted hover:text-danger"
          >
            {t("accounts.import.drop.clear")}
          </button>
        )}
      </div>

      {files.length > 0 && (
        <ul className="max-h-40 space-y-0.5 overflow-auto rounded-md border border-border bg-bg-primary/40 p-2 text-xs">
          {files.map((f, i) => {
            const kind = fileKind(f, plan);
            return (
              <li
                key={`${f.name}-${i}`}
                className={cn("flex items-center gap-2", kind === "skip" && "opacity-50")}
              >
                <span
                  className={cn(
                    "rounded px-1.5 py-0.5 font-mono text-[11px] uppercase tracking-[0.04em]",
                    kind === "archive"
                      ? "bg-accent/15 text-accent"
                      : kind === "session"
                        ? "bg-success/15 text-success"
                        : "bg-bg-elevated text-fg-secondary",
                  )}
                >
                  {kind === "skip" ? t("accounts.import.kind.skip") : kind}
                </span>
                <span className="flex-1 truncate font-mono text-fg-primary">{f.name}</span>
                <span className="text-fg-muted">
                  {f.size < 1024 ? "<1 KB" : `${Math.round(f.size / 1024)} KB`}
                </span>
                <button
                  type="button"
                  onClick={() => onChange(files.filter((x) => x !== f))}
                  className="px-1 text-fg-muted hover:text-danger"
                  aria-label={t("common.remove")}
                >
                  <X size={14} aria-hidden="true" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Recursively walk a dropped FileSystemEntry, returning all File objects.
 * readEntries only returns one batch (~100 items) per call, so we loop until
 * it returns empty — otherwise large folders silently truncate. */
async function walkEntry(entry: FileSystemEntry): Promise<File[]> {
  if (entry.isFile) {
    return new Promise<File[]>((resolve, reject) => {
      (entry as FileSystemFileEntry).file(
        (f) => resolve([f]),
        (err) => reject(err),
      );
    });
  }
  if (entry.isDirectory) {
    const reader = (entry as FileSystemDirectoryEntry).createReader();
    const out: File[] = [];
    let done = false;
    while (!done) {
      const batch = await new Promise<FileSystemEntry[]>((resolve, reject) =>
        reader.readEntries(resolve, reject),
      );
      if (batch.length === 0) {
        done = true;
      } else {
        const lists = await Promise.all(batch.map(walkEntry));
        for (const list of lists) out.push(...list);
      }
    }
    return out;
  }
  return [];
}
